import React, { useEffect, useState } from 'react';
import { App as AntApp, Button, Card, Flex, Switch, Tag, Typography } from 'antd';
import { CreditCardOutlined } from '@ant-design/icons';
import { type AccessState, formatPrice, SUBSCRIPTION_PLANS } from '@lingoping/core';
import { isDevAuthMock } from '../../auth/index.js';
import { CANCEL_PAGE_PATH } from './brand.js';
import { formatDate } from './dates.js';
import { summarizeAccess } from './accessText.js';
import {
  cancelOwnSubscription,
  getReminderEmails,
  openBillingPortalUrl,
  setReminderEmails,
} from './billingApi.js';

const { Text } = Typography;

const TAG_COLOR = { ok: 'success', warn: 'warning', bad: 'error', neutral: 'processing' } as const;

interface Props {
  access: AccessState;
  refreshAccess: () => Promise<AccessState | null>;
}

/** Settings > Subscription: where the learner stands, card, cancel, trial reminder emails. */
export const SubscriptionCard: React.FC<Props> = ({ access, refreshAccess }) => {
  const { message, modal } = AntApp.useApp();
  const summary = summarizeAccess(access);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reminders, setReminders] = useState<boolean | null>(isDevAuthMock() ? true : null);
  const [savingReminders, setSavingReminders] = useState(false);

  // The reminder emails only go to learners who have not subscribed.
  const showReminders = !summary.hasSubscription;
  useEffect(() => {
    if (!showReminders) return;
    if (isDevAuthMock()) return;
    let live = true;
    getReminderEmails()
      .then((on) => live && setReminders(on))
      .catch((err) => console.warn('[LingoPing] Could not read the email setting:', err));
    return () => {
      live = false;
    };
  }, [showReminders]);

  const changeCard = async () => {
    setPortalLoading(true);
    try {
      window.location.assign(await openBillingPortalUrl());
    } catch (err) {
      console.error('Could not open the billing portal:', err);
      message.error("The card page couldn't be opened. Please try again.");
      setPortalLoading(false);
    }
  };

  const doCancel = async () => {
    setCancelling(true);
    try {
      const result = isDevAuthMock()
        ? { ok: true as const, received_at: new Date().toISOString() }
        : await cancelOwnSubscription();
      void refreshAccess();
      const end = formatDate(access.subscription?.current_period_end ?? access.until);
      modal.success({
        title: 'Your cancellation is in.',
        content: `Received ${new Date(result.received_at).toLocaleString('en-GB', {
          dateStyle: 'long',
          timeStyle: 'short',
        })}.${end ? ` You keep access until ${end}.` : ''}`,
      });
    } catch (err) {
      console.error('Cancelling failed:', err);
      message.error("Your subscription couldn't be cancelled. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const confirmCancel = () => {
    const end = formatDate(access.subscription?.current_period_end ?? access.until);
    modal.confirm({
      title: 'Cancel your subscription?',
      content: end ? `You keep access until ${end}.` : undefined,
      okText: 'Cancel subscription',
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      onOk: doCancel,
    });
  };

  const handleReminders = async (on: boolean) => {
    const before = reminders;
    setReminders(on);
    setSavingReminders(true);
    try {
      if (!isDevAuthMock()) await setReminderEmails(on);
    } catch (err) {
      console.error('Could not save the email setting:', err);
      setReminders(before);
      message.error("That setting couldn't be saved. Please try again.");
    } finally {
      setSavingReminders(false);
    }
  };

  const price = SUBSCRIPTION_PLANS.monthly;

  return (
    <Card title="Subscription">
      <Flex vertical gap={16}>
        <Flex align="center" gap={10} wrap>
          <Tag
            color={TAG_COLOR[summary.tone]}
            style={{ margin: 0, fontSize: 14, padding: '2px 10px' }}
          >
            {summary.text}
          </Tag>
        </Flex>
        <Text type="secondary">
          {formatPrice(price.amountCents, price.currency)} / month, VAT included
        </Text>

        <Flex gap={8} wrap>
          {!summary.hasSubscription && access.state !== 'grant' && (
            <Button type="primary" href="#subscribe">
              Subscribe
            </Button>
          )}
          {summary.hasSubscription && (
            <Button icon={<CreditCardOutlined />} loading={portalLoading} onClick={changeCard}>
              Change card
            </Button>
          )}
          {summary.cancellable && (
            <Button danger loading={cancelling} onClick={confirmCancel}>
              Cancel subscription
            </Button>
          )}
        </Flex>

        {showReminders && (
          <Flex align="center" gap={10}>
            <Switch
              id="trial-reminders"
              checked={reminders ?? false}
              disabled={reminders === null}
              loading={savingReminders}
              onChange={(on) => void handleReminders(on)}
            />
            <label htmlFor="trial-reminders">Trial reminder emails</label>
          </Flex>
        )}

        <a href={CANCEL_PAGE_PATH} lang="de" style={{ fontSize: 13, color: '#475569' }}>
          Verträge hier kündigen
        </a>
      </Flex>
    </Card>
  );
};
